import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAlert } from '../context/AlertContext';
import { getEvents } from '../services/eventService';
import EventCard from '../components/EventCard';
import Modal from '../components/Modal';
import RSVPForm from '../components/RSVPForm';

const EventsPage = () => {
  const { showAlert } = useAlert();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const cardRef = useRef(null);
  const shouldScroll = useRef(true);
  const lastLocationKey = useRef(null);

  useEffect(() => {
    loadEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to first event if navigated from "Register Now"
  useEffect(() => {
    // Check if this is a new navigation (different location key)
    const isNewNavigation = lastLocationKey.current !== location.key;
    
    if (!loading && events.length > 0 && location.state?.autoOpenFirst && isNewNavigation) {
      lastLocationKey.current = location.key; // Mark this navigation as handled
      shouldScroll.current = false; // Disable default scroll
      // Force scroll to first event card
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, [loading, events, location]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll after events are loaded and rendered (only if not from Register Now)
  useEffect(() => {
    if (!loading && events.length > 0 && shouldScroll.current && !location.state?.autoOpenFirst) {
      shouldScroll.current = false;
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [loading, events]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await getEvents();
      // Show active, upcoming events only (hide events whose date has passed),
      // sorted by date. Events with no date are kept as a safeguard.
      const today = new Date().toISOString().split('T')[0];
      const activeEvents = data
        .filter((event) => event.isActive && (!event.date || event.date >= today))
        .sort((a, b) => {
          // Sort by date ascending (earliest first)
          if (!a.date || !b.date) return 0;
          return new Date(a.date) - new Date(b.date);
        });
      setEvents(activeEvents);
    } catch (error) {
      showAlert(error.message || 'Error loading events', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = (event) => {
    setSelectedEvent(event);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const handleRSVPSuccess = () => {
    shouldScroll.current = false;
    loadEvents();
  };

  if (loading) {
    return (
      <div className={`events-grid ${events.length === 1 ? "events-grid-single" : ""}`}>
        <p>Loading events...</p>
      </div>
    );
  }

  return (
    <>
      <div ref={cardRef} className={`events-grid ${events.length === 1 ? "events-grid-single" : ""}`}>
        {events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏌️</div>
            <h3>No upcoming events</h3>
            <p>Check back soon for tournament dates!</p>
          </div>
        ) : (
          events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onRSVP={handleRSVP}
            />
          ))
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={handleModalClose}
        title="RSVP for Tournament"
      >
        {selectedEvent && (
          <RSVPForm
            event={selectedEvent}
            onClose={handleModalClose}
            onSuccess={handleRSVPSuccess}
          />
        )}
      </Modal>
    </>
  );
};

export default EventsPage;
